import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, ExternalLink, Copy, CheckCircle2, Loader2, AlertTriangle, Radio } from "lucide-react";

export default function EditorialPitchDialog({ release, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(release?.editorial_brief || null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!release) return;
    setLoading(true);
    try {
      const r = await api.post(`/releases/${release.release_id}/editorial-pitch`, {});
      setBrief(r.data);
      if (r.data.lead_time_warning) {
        toast.warning(r.data.lead_time_warning);
      } else {
        toast.success("Pitch ready — paste into Spotify for Artists.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Pitch generation failed";
      toast.error(typeof detail === "string" ? detail : "Pitch generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text, label = "Copied") => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
        toast(label);
      },
      () => toast.error("Copy failed"),
    );
  };

  if (!release) return null;

  const pitchLen = brief?.pitch?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm w-[calc(100vw-1rem)] max-w-3xl text-white max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="editorial-pitch-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight flex items-center gap-3">
            <Radio className="text-[#1DB954]" size={24} />
            Spotify editorial pitch · {release.title}
          </DialogTitle>
        </DialogHeader>

        <p className="text-white/55 text-sm leading-relaxed mb-5">
          Drafts the 500-char pitch + mood/instrument/style tags for Spotify for Artists.
          Pitch <span className="text-white">7+ days before release date</span> for the best
          chance at Editorial consideration (<em>Night Pass, Night Rider Mix, After Hours</em>).
        </p>

        <button
          className="btn-amber w-full mb-5"
          onClick={generate}
          disabled={loading}
          data-testid="editorial-generate-btn"
        >
          {loading ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : (
            <><Music size={14} /> {brief ? "Regenerate pitch" : "Generate pitch"}</>
          )}
        </button>

        {brief?.lead_time_warning && (
          <div className="mb-5 p-4 border border-amber-500/30 bg-amber-500/10 rounded-sm flex gap-3">
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="text-amber-100 text-sm leading-relaxed">{brief.lead_time_warning}</div>
          </div>
        )}

        {brief && (
          <div className="space-y-5 fade-in">
            {/* The pitch text */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono text-white/45">Pitch ({pitchLen}/500 chars)</div>
                <button
                  className="label-mono text-white/60 hover:text-white flex items-center gap-1"
                  onClick={() => copyText(brief.pitch, "Pitch copied")}
                >
                  <Copy size={11} /> Copy pitch
                </button>
              </div>
              <div
                className="p-4 border border-[#1DB954]/30 bg-[#1DB954]/5 text-white/95 text-sm leading-relaxed whitespace-pre-wrap rounded-sm"
                data-testid="editorial-pitch-text"
              >
                {brief.pitch}
              </div>
            </div>

            {/* Tag grids */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="label-mono text-white/45 mb-2">Genres (pick 1-3)</div>
                <div className="flex flex-wrap gap-1.5">
                  {brief.genres.map((g) => (
                    <span key={g} className="badge bg-[#1DB954]/10 border-[#1DB954]/40 text-[#4ed784]">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-mono text-white/45 mb-2">Style of (pick 1-3)</div>
                <div className="flex flex-wrap gap-1.5">
                  {brief.style_of.map((a) => (
                    <span key={a} className="badge bg-white/5 border-white/10 text-white/85">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-mono text-white/45 mb-2">Moods</div>
                <div className="flex flex-wrap gap-1.5">
                  {brief.moods.map((m) => (
                    <span key={m} className="badge bg-white/5 border-white/10 text-white/85">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-mono text-white/45 mb-2">Instruments</div>
                <div className="flex flex-wrap gap-1.5">
                  {brief.instruments.map((i) => (
                    <span key={i} className="badge bg-white/5 border-white/10 text-white/85">
                      {i}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="label-mono text-white/45 mb-2">Target playlists (mention in pitch if natural)</div>
              <div className="flex flex-wrap gap-1.5">
                {brief.target_playlists.map((p) => (
                  <span key={p} className="badge bg-[#1DB954]/10 border-[#1DB954]/40 text-[#4ed784]">
                    {p}
                  </span>
                ))}
              </div>
            </div>

            {/* Launch */}
            <div className="flex flex-col md:flex-row gap-3 pt-4 border-t border-white/[0.06]">
              <a
                href={brief.s4a_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-amber flex-1"
                data-testid="editorial-open-s4a"
              >
                <ExternalLink size={14} /> Open Spotify for Artists
              </a>
              <button
                className="btn-ghost-line flex-1"
                onClick={() => copyText(brief.pitch, "Pitch copied")}
                data-testid="editorial-copy-pitch"
              >
                {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy pitch</>}
              </button>
            </div>
            <div className="text-xs text-white/45 leading-relaxed">
              In Spotify for Artists → <span className="text-white/70">Music</span> → pick this unreleased track →
              <span className="text-white/70"> Pitch a song</span>. Paste the pitch above, tick the matching tags, hit Submit.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
